/**
 * Pricing configuration for response levels
 * Values are read from environment variables with fallback defaults
 */

// Read prices from environment variables (set in .env file)
const PRICE_QUICK = Number(import.meta.env.VITE_PRICE_QUICK) || 9;
const PRICE_STANDARD = Number(import.meta.env.VITE_PRICE_STANDARD) || 19;
const PRICE_INDEPTH = Number(import.meta.env.VITE_PRICE_INDEPTH) || 29;
const CURRENCY_SYMBOL = import.meta.env.VITE_CURRENCY_SYMBOL || '₹';
const SIGNUP_REWARD = Number(import.meta.env.VITE_SIGNUP_REWARD) || 51;
const DISCUSSION_REWARD = Number(import.meta.env.VITE_DISCUSSION_REWARD) || 21;
const REFERRAL_REWARD_REFERRER = Number(import.meta.env.VITE_REFERRAL_REWARD_REFERRER) || 50;
const REFERRAL_REWARD_REFEREE = Number(import.meta.env.VITE_REFERRAL_REWARD_REFEREE) || 50;

/**
 * Response level configuration with pricing
 */
export const RESPONSE_LEVELS = [
  { value: 'Quick', label: `Quick (${CURRENCY_SYMBOL}${PRICE_QUICK})`, price: PRICE_QUICK },
  { value: 'Standard', label: `Standard (${CURRENCY_SYMBOL}${PRICE_STANDARD})`, price: PRICE_STANDARD },
  { value: 'In-depth', label: `In-depth (${CURRENCY_SYMBOL}${PRICE_INDEPTH})`, price: PRICE_INDEPTH },
];

/**
 * Response level descriptions for info tooltips
 */
export const RESPONSE_LEVEL_INFO = {
  Quick: 'Simple, easy-to-understand answers',
  Standard: 'Detailed explanations with context',
  'In-depth': 'Comprehensive analysis with advanced insights',
};

/**
 * Get response level info for step tooltips
 */
export const getResponseLevelStepInfo = () => [
  { title: `Quick (${CURRENCY_SYMBOL}${PRICE_QUICK})`, desc: 'Simple answers per question' },
  { title: `Standard (${CURRENCY_SYMBOL}${PRICE_STANDARD})`, desc: 'Detailed explanations per question' },
  { title: `In-depth (${CURRENCY_SYMBOL}${PRICE_INDEPTH})`, desc: 'Comprehensive analysis per question' },
];

/**
 * Default response level
 */
export const DEFAULT_RESPONSE_LEVEL = 'Quick';

/**
 * Pricing utilities
 */
export const pricing = {
  quick: PRICE_QUICK,
  standard: PRICE_STANDARD,
  indepth: PRICE_INDEPTH,
  currency: CURRENCY_SYMBOL,
  signupReward: SIGNUP_REWARD,
  discussionReward: DISCUSSION_REWARD,
  referralRewardReferrer: REFERRAL_REWARD_REFERRER,
  referralRewardReferee: REFERRAL_REWARD_REFEREE,
  // Calculate free questions based on signup reward and lowest price
  get freeQuestions() {
    return Math.floor(SIGNUP_REWARD / PRICE_QUICK);
  },
  // Calculate free questions from discussion reward
  get discussionFreeQuestions() {
    return Math.floor(DISCUSSION_REWARD / PRICE_QUICK);
  },
};

/**
 * Quick Recharge option interface
 */
export interface QuickRechargeOption {
  amount: number;
  questions: number;
  popular?: boolean;
  bonus?: number; // Extra bonus credits on recharge
}

/**
 * Quick Recharge configuration
 * Single list - chat page shows first 3, payment page shows all
 * Uses psychological pricing (₹X9 pattern)
 */
export const QUICK_RECHARGE_OPTIONS: QuickRechargeOption[] = [
  { amount: 99, questions: Math.floor(99 / PRICE_QUICK) },
  { amount: 249, questions: Math.floor((249 + 51) / PRICE_QUICK), popular: true, bonus: 51 },
  { amount: 499, questions: Math.floor(499 / PRICE_QUICK) },
  { amount: 999, questions: Math.floor((999 + 101) / PRICE_QUICK), bonus: 101 },
  { amount: 1999, questions: Math.floor(1999 / PRICE_QUICK) },
  { amount: 2999, questions: Math.floor((2999 + 301) / PRICE_QUICK), bonus: 301 },
];

// Payment gateway fee percentage. Backend grosses up the payable amount so the
// received base amount still nets out after provider fees.
export const GATEWAY_FEE_PERCENT = 2;

export const calculateGatewayTotal = (baseAmount: number): number => {
  const feeRate = GATEWAY_FEE_PERCENT / 100;
  return Number((baseAmount / (1 - feeRate)).toFixed(2));
};

export const calculateGatewayFee = (baseAmount: number): number => {
  return Number((calculateGatewayTotal(baseAmount) - baseAmount).toFixed(2));
};
